import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Typography, Box, Button } from '@mui/material';
import LogoAIVoice from '@src/assets/logo/logo-aivoice.png';
import QRVoiceCloningDevImg from '@src/assets/images/qr-create-vc-dev.png';
import QRVoiceCloningUatImg from '@src/assets/images/qr-create-vc-uat.png';
import QRVoiceCloningProdImg from '@src/assets/images/qr-create-vc-prod.png';
import { TIME_EXPIRE_QR_VOICE_CLONING } from '@src/constants/voiceCloning';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { ReplayRounded } from '@mui/icons-material';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { ENV } from '@src/configs';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import useUserInfo from '@src/hooks/useTtsUser';
import { StyledQRDialog } from './index.style';

const QRDialog = ({
  open,
  onClose,
  urlToShow,
  qrRedirectUrl,
  qrExpireDate,
  refreshQRCode,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { ttsUser } = useUserInfo();
  const { getFeatureValue } = useFeatureFlags();
  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const loginByQR = validateUserPackageCanUseVoiceCloning({
    ttsUser,
    isUserInWhiteList: userInFeatureVoiceCloning,
  });

  const { t } = useTranslation();
  const imagesByEnv = {
    dev: QRVoiceCloningDevImg,
    uat: QRVoiceCloningUatImg,
    prod: QRVoiceCloningProdImg,
  };
  const qrCodeUrl = urlToShow || imagesByEnv[ENV];

  const [isExpired, setIsExpired] = useState(
    qrExpireDate && moment().isAfter(qrExpireDate),
  );

  const handleCloseDialog = () => {
    setIsExpired(false);
    onClose();
  };

  useEffect(() => {
    if (!open) return null;
    if (!loginByQR) return null;

    if (qrExpireDate && !moment().isAfter(qrExpireDate)) setIsExpired(false);

    const timeoutExpire = setTimeout(() => {
      setIsExpired(true);
    }, TIME_EXPIRE_QR_VOICE_CLONING);

    return () => clearTimeout(timeoutExpire); // Clear interval when component unmounts
  }, [open, qrExpireDate]);

  const renderRefreshButton = () => (
    <Button
      variant="contained"
      className="refresh-button"
      onClick={() => refreshQRCode(qrRedirectUrl)}
    >
      <ReplayRounded
        sx={{ width: '24px', height: '24px', marginRight: '8px' }}
      />
      {t('getNewQRCode')}
    </Button>
  );

  return (
    <StyledQRDialog open={open} onClose={handleCloseDialog}>
      <Box className="box-dialog">
        <Typography className="qr-title">{t('titleQRVoiceCloning')}</Typography>
        <Typography
          className="qr-description"
          dangerouslySetInnerHTML={{ __html: t('descriptionQRVoiceCloning') }}
        />
        <Box
          display="flex"
          flexDirection="column"
          gap="16px"
          justifyContent="center"
          alignItems="center"
        >
          {!loginByQR && (
            <Box className="box-qr-img">
              <img src={qrCodeUrl} alt="QR Code" className="qr-img" />
            </Box>
          )}

          {loginByQR && (
            <>
              <Box className={`box-qr-img ${isExpired ? 'expired' : ''}`}>
                <QRCode
                  value={qrCodeUrl}
                  size={176}
                  renderAs="svg"
                  imageSettings={{
                    src: LogoAIVoice,
                    x: null,
                    y: null,
                    height: 40,
                    width: 40,
                  }}
                />
              </Box>
              {!isExpired ? (
                renderRefreshButton()
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="8px"
                  justifyItems="center"
                  alignItems="center"
                >
                  <Typography className="qr-expired">
                    {t('qrCodeExpired')}
                  </Typography>
                  {renderRefreshButton()}
                </Box>
              )}
            </>
          )}
          <Typography className="text-or">{t('or')}</Typography>
          <Box className="box-qr-beta">
            <Typography
              className="qr-beta-text"
              dangerouslySetInnerHTML={{
                __html: t('downloadTheApplicationHere'),
              }}
            />
          </Box>
        </Box>
        {loginByQR ? (
          <Typography
            className="qr-refresh-text"
            dangerouslySetInnerHTML={{ __html: t('qrRefreshText') }}
          />
        ) : (
          <Box className="box-qr-note">
            <Typography className="qr-note">
              {t('noteQRVoiceCloning')}
            </Typography>
          </Box>
        )}
      </Box>
    </StyledQRDialog>
  );
};

export default QRDialog;
